import { useRef, useEffect } from 'react'
import { AnimatePresence } from 'framer-motion'
import { ChatMessage } from './ChatMessage'
import type { Message } from '../../../../types/chat.types'
import type { Agent } from '../../../../services/agentService'
import { ChatWelcomeScreen } from './ChatWelcomeScreen'

interface ChatMessagesProps {
    messages: Message[]
    isLoading?: boolean
    agentName: string
    agentAvatar?: string | null
    agent?: Agent | null
    onCopy?: (content: string, index: number) => void
    onFeedback?: (index: number, feedback: 'liked' | 'disliked') => void
    copiedMessageId?: number | null
    streamingMessage?: {
        content: string
        isStreaming: boolean
    } | null
}

export const ChatMessages: React.FC<ChatMessagesProps> = ({
    messages,
    agentName,
    agentAvatar,
    agent,
    onCopy,
    onFeedback,
    copiedMessageId,
    streamingMessage
}) => {
    const chatContainerRef = useRef<HTMLDivElement>(null)

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
        }
    }, [messages, streamingMessage])

    // Loading state removed as requested
    // if (isLoading && messages.length === 0) {
    //    return (
    //        <div className="flex-1 flex items-center justify-center">
    //            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    //        </div>
    //    )
    // }

    // Empty state - Welcome Screen
    if (messages.length === 0 && !streamingMessage && agent) {
        return <ChatWelcomeScreen agent={agent} />
    }

    return (
        <div
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto p-4 space-y-4 relative touch-pan-y"
            style={{ overscrollBehavior: 'contain' }}
        >
            <AnimatePresence>
                {messages.map((message, index) => (
                    <ChatMessage
                        key={index}
                        message={message}
                        index={index}
                        agentName={agentName}
                        agentAvatar={agentAvatar}
                        agent={agent}
                        onCopy={onCopy}
                        onFeedback={onFeedback}
                        copiedMessageId={copiedMessageId}
                    />
                ))}

                {/* Streaming message - Hide if identical to last message (deduplication fallback) */}
                {streamingMessage && streamingMessage.isStreaming &&
                    (!messages.length || messages[messages.length - 1].content !== streamingMessage.content) && (
                        <ChatMessage
                            key="streaming"
                            message={{
                                id: 'streaming',
                                conversation_id: 'streaming',
                                role: 'assistant',
                                content: streamingMessage.content,
                                created_at: new Date().toISOString(),
                                isHtml: false
                            }}
                            index={-1}
                            agentName={agentName}
                            agentAvatar={agentAvatar}
                            agent={agent}
                        />
                    )}
            </AnimatePresence>
        </div>
    )
}
